<template>
  <div id="graph" ref="graph"></div>
</template>

<script>
import ForceGraph3D from "3d-force-graph";
import {
  Mesh,
  BoxGeometry,
  TetrahedronGeometry,
  CylinderGeometry,
  SphereGeometry,
  MeshLambertMaterial
} from "three";

import MPCStore from "../stores/MPCStore";
import {
  BUS_I,
  PD,
  QD,
  GS,
  BS,
  GEN_BUS,
  F_BUS,
  T_BUS,
  TAP
} from "../stores/idx";

export default {
  name: "Graph",
  mounted() {
    let width = this.$el.offsetWidth;
    let height = this.$el.offsetHeight;

    let busNodes = MPCStore.methods.getBuses().map((bus, i) => ({
      device: "bus",
      idx: i,
      id: bus[BUS_I]
    }));
    let loadNodes = MPCStore.methods
      .getBuses()
      .filter(isLoad)
      .map(bus => ({
        device: "load",
        bus_i: bus[BUS_I],
        id: "l" + bus[BUS_I]
      }));
    let loadConns = MPCStore.methods
      .getBuses()
      .filter(isLoad)
      .map(bus => ({
        device: "load",
        bus_i: bus[BUS_I],
        source: bus[BUS_I],
        target: "l" + bus[BUS_I]
      }));
    let shuntNodes = MPCStore.methods
      .getBuses()
      .filter(isShunt)
      .map(bus => ({
        device: "shunt",
        bus_i: bus[BUS_I],
        id: "s" + bus[BUS_I]
      }));
    let shuntConns = MPCStore.methods
      .getBuses()
      .filter(isShunt)
      .map(bus => ({
        device: "shunt",
        bus_i: bus[BUS_I],
        source: bus[BUS_I],
        target: "s" + bus[BUS_I]
      }));

    let genNodes = MPCStore.methods.getGens().map((_, i) => ({
      device: "gen",
      idx: i,
      id: "g" + (i + 1)
    }));
    let genConns = MPCStore.methods.getGens().map((gen, i) => ({
      device: "gen",
      idx: i,
      source: "g" + (i + 1),
      target: gen[GEN_BUS]
    }));

    let branches = MPCStore.methods.getBranches().map((br, i) => ({
      device: br[TAP] !== 0 ? "tfmr" : "line",
      idx: i,
      source: br[F_BUS],
      target: br[T_BUS]
    }));

    const data = {
      nodes: [...busNodes, ...loadNodes, ...shuntNodes, ...genNodes],
      links: [...branches, ...loadConns, ...shuntConns, ...genConns]
    };

    const graph = ForceGraph3D();
    graph(this.$refs["graph"])
      .graphData(data)
      .width(width)
      .height(height)
      .backgroundColor("#fff")
      .showNavInfo(false)
      .linkWidth(function({ device }) {
        switch (device) {
          case "load":
          case "shunt":
          case "gen":
            return 1.5;
          default:
            return 2.5;
        }
      })
      .linkColor(function({ device }) {
        switch (device) {
          case "load":
            return loadColor;
          case "shunt":
            return shuntColor;
          case "gen":
            return genColor;
          case "tfmr":
            return tfmrColor;
          case "line":
            return lineColor;
          default:
            return "black";
        }
      })
      .linkOpacity(1)
      .nodeThreeObject(function({ device }) {
        switch (device) {
          case "bus":
            return new Mesh(
              new BoxGeometry(10, 10, 10),
              new MeshLambertMaterial({ color: busColor })
            );
          case "load":
            return new Mesh(
              new TetrahedronGeometry(10),
              new MeshLambertMaterial({ color: loadColor })
            );
          case "shunt":
            return new Mesh(
              new CylinderGeometry(5, 5, 10, 16),
              new MeshLambertMaterial({ color: shuntColor })
            );
          case "gen":
            return new Mesh(
              new SphereGeometry(5, 32, 16),
              new MeshLambertMaterial({ color: genColor })
            );
          default:
            return false;
        }
      });

    const busColor = "#2962ff";
    const loadColor = "#ffd600";
    const shuntColor = "#00c853";
    const genColor = "#ff1744";
    const tfmrColor = "#aa00ff";
    const lineColor = "#00b8d4";

    function isLoad(bus) {
      return bus[PD] !== 0 || bus[QD] !== 0;
    }
    function isShunt(bus) {
      return bus[GS] !== 0 || bus[BS] !== 0;
    }
  }
};
</script>

<style scoped>
#graph {
  flex: 1;
}
</style>
